import type { HidChannel, HidReportListener } from '../../../channels/hid/HidChannel'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'

/** CH315 USB 协议的 Report ID、长度与 ACK 常量。 */
export const CH315_HID_REPORT = {
  /** 端点 2 普通中断 IN/OUT 报告。 */
  interrupt: 0x08,
  /** 端点 2 批量 IN/OUT 报告。 */
  bulk: 0x07,
  /** 含 Report ID 的普通中断包总长度。 */
  interruptPacketBytes: 64,
  /** WebHID `sendReport` / `inputreport.data` 中不含 Report ID 的普通包长度。 */
  interruptDataBytes: 63,
  /** 普通中断包内可承载的命令数据上限。 */
  interruptPayloadMaxBytes: 59,
  /** 含 Report ID 的批量包总长度。 */
  bulkPacketBytes: 512,
  /** WebHID `sendReport` / `inputreport.data` 中不含 Report ID 的批量包长度。 */
  bulkDataBytes: 511,
  /** 批量包内可承载的命令数据上限。 */
  bulkPayloadMaxBytes: 504,
  /** 0xAF 命令头之外可写入的数据上限。 */
  bulkWriteDataMaxBytes: 496,
} as const

/** CH315 命令字。 */
export const CH315_COMMAND = {
  setMode: 0xa1,
  getMode: 0xa2,
  getFirmwareInfo: 0xa3,
  readEeprom: 0xa4,
  writeEeprom: 0xa5,
  resetDevice: 0xa7,
  enterFirmwareUpdate: 0xa8,
  getBattery: 0xaa,
  restoreFactoryDefaults: 0xad,
  readFlashBulk: 0xae,
  writeFlashBulk: 0xaf,
  enterCalibration: 0xb0,
  saveCalibration: 0xb1,
  exitCalibration: 0xb2,
  deleteConfig: 0xd0,
  startRfTest: 0xd1,
  selectConfig: 0xd2,
  getCurrentConfig: 0xd3,
  backlightTest: 0xd4,
  addConfig: 0xd5,
  deleteUserStaticEffect: 0xd6,
  readRfFlash: 0xe3,
  writeRfFlash: 0xe4,
  userReport: 0xf0,
} as const

/** 统一 ACK 的定义。 */
export const CH315_ACK = {
  failed: 0x00,
  success: 0x01,
  busy: 0x02,
  retry: 0x03,
} as const

export const CH315_DEVICE_SELECT = {
  host: 0x01,
  slave: 0x02,
  hostRf: 0x04,
  slaveRf: 0x08,
  all: 0xff,
} as const

export const CH315_MODE = {
  g24: 0x01,
  ble: 0x02,
  usb: 0x03,
  fcc: 0x04,
} as const

export type CH315Ack = (typeof CH315_ACK)[keyof typeof CH315_ACK]
export type CH315Mode = (typeof CH315_MODE)[keyof typeof CH315_MODE]

export interface CH315InterruptPacket {
  reportId: number
  packetSequence: number
  payloadLength: number
  payload: Uint8Array
  checksum: number
  checksumValid: boolean
  /** 不包含 Report ID 的原始数据，范围为包序号至 checksum。 */
  raw: Uint8Array
}

export interface CH315CommandResponse {
  command: number
  ack: CH315Ack
  packet: CH315InterruptPacket
  /** 自命令字开始的完整响应载荷。 */
  payload: Uint8Array
  attempts: number
}

export interface CH315FirmwareInfo {
  deviceSelect: number
  versionBytes: readonly [number, number, number, number]
  version: string
  usbPid: number
}

export interface CH315BatteryInfo {
  deviceSelect: number
  valid: boolean
  percent: number
}

export interface CH315MemoryReadResult {
  address: number
  length: number
  data: Uint8Array
  response: CH315CommandResponse
}

export interface CH315UserStatusReport {
  connectionMode: number
  configIndex: number
  backlightMode: number
  backlightLevel: number
  backlightSpeed: number
  backlightColor: number
  sleepMinutes: number
  reportRate: number
  batteryPercent: number
  functionWord: number
  usbPid: number
  deviceStatus: number
  connectionRssi: number
  sideLightMode: number
  sideLightLevel: number
  sideLightSpeed: number
  sideLightColor: number
}

export interface CH315CalibrationKeyReport {
  keySerial: number
  calibrationStatus: number
  zeroDelta: number
  maxDelta: number
  lta: number
  normalizedDelta: number
  release: number
}

export type CH315IncomingReport =
  | { type: 'packet'; packet: CH315InterruptPacket }
  | { type: 'user-status'; packet: CH315InterruptPacket; status: CH315UserStatusReport }
  | {
      type: 'calibration'
      packet: CH315InterruptPacket
      packetSequence: number
      releaseFlag: number
      keys: CH315CalibrationKeyReport[]
    }

export interface CH315CommandOptions {
  /** 默认 100 ms，与协议中 0.1 s 的确认超时一致。 */
  timeoutMs?: number
  /** 超时或 ACK=busy/retry 后的最大重传次数，默认 3。 */
  maxRetries?: number
  /** 仅用于原始命令调试；正常接口遇到 ACK 失败会抛出错误。 */
  requireSuccess?: boolean
}

export interface CH315ProtocolOptions {
  /**
   * A4 请求长度字段宽度。PDF 的字节表为 1 B、文字为 uint16；默认沿用当前
   * 2 B 兼容解释，联调时可显式切换为 1 B。
   */
  normalReadLengthBytes?: 1 | 2
  /** E3 与 A4 存在相同冲突，独立配置以便抓包验证。 */
  rfReadLengthBytes?: 1 | 2
}

const NORMAL_READ_MAX_LENGTH = 52
const NORMAL_WRITE_SAFE_MAX_LENGTH = 53
const BULK_RW_MAX_LENGTH = 496
const RF_WRITE_MAX_LENGTH = 24

/**
 * 构造 WebHID 所需的 63 B 中断包数据。
 *
 * PDF 中的 Byte 0 是 HID Report ID；浏览器 API 已将其作为第一个参数传递，
 * 因此返回值从 `pkt_sn` 开始，不重复写入 0x08。
 */
export function encodeCH315InterruptFrame(
  packetSequence: number,
  payload: Uint8Array,
  reportId: number = CH315_HID_REPORT.interrupt,
): Uint8Array {
  if (payload.byteLength > CH315_HID_REPORT.interruptPayloadMaxBytes) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `CH315 interrupt payload exceeds ${CH315_HID_REPORT.interruptPayloadMaxBytes} bytes`,
    )
  }

  const frame = new Uint8Array(CH315_HID_REPORT.interruptDataBytes)
  frame[0] = packetSequence & 0xff
  frame[1] = payload.byteLength & 0xff
  frame[2] = (payload.byteLength >>> 8) & 0xff
  frame.set(payload, 3)

  const checksumIndex = 3 + payload.byteLength
  frame[checksumIndex] = calculateCH315Checksum(reportId, frame.subarray(0, checksumIndex))
  return frame
}

/** 解析普通中断包；同时兼容 WebHID 的 63 B data 和抓包工具附带 Report ID 的 64 B 数据。 */
export function decodeCH315InterruptFrame(
  reportId: number,
  data: DataView | Uint8Array,
): CH315InterruptPacket {
  const input =
    data instanceof DataView ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : data
  const frame = stripCapturedReportId(reportId, input, CH315_HID_REPORT.interruptPacketBytes)

  if (frame.byteLength < 4) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      'CH315 interrupt frame is shorter than header',
    )
  }

  const payloadLength = frame[1] | (frame[2] << 8)
  if (payloadLength > CH315_HID_REPORT.interruptPayloadMaxBytes) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `CH315 interrupt payload length ${payloadLength} exceeds protocol limit`,
    )
  }

  const payloadStart = 3
  const payloadEnd = payloadStart + payloadLength
  const checksumIndex = payloadEnd
  if (checksumIndex >= frame.byteLength) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 interrupt frame is truncated')
  }

  const checksum = frame[checksumIndex]
  const expectedChecksum = calculateCH315Checksum(reportId, frame.subarray(0, checksumIndex))
  return {
    reportId,
    packetSequence: frame[0],
    payloadLength,
    payload: frame.slice(payloadStart, payloadEnd),
    checksum,
    checksumValid: checksum === expectedChecksum,
    raw: frame.slice(0, checksumIndex + 1),
  }
}

/** 计算“Report ID + 包序号 + 长度 + 载荷”的低 8 位累加和。 */
export function calculateCH315Checksum(reportId: number, bytes: Uint8Array): number {
  let sum = reportId & 0xff
  for (const value of bytes) sum = (sum + value) & 0xff
  return sum
}

/** 将 0x00000101 形式的 BCD 版本转为可读字符串。 */
export function formatCH315BcdVersion(bytes: readonly number[]): string {
  // 协议示例 V1.01 的字节序为 01 01 00 00，即低有效字节在前。
  const digits = [...bytes]
    .reverse()
    .map((value) => `${(value >>> 4) & 0x0f}${value & 0x0f}`)
    .join('')
  const normalized = digits.replace(/^0+(?=\d)/, '') || '0'
  if (normalized.length <= 2) return `V${normalized}`
  return `V${normalized.slice(0, -2)}.${normalized.slice(-2)}`
}

/**
 * CH315 的协议客户端。
 *
 * 与旧鼠标的 Output Report + Feature Report 轮询不同，CH315 的命令应答通过
 * endpoint 2 的 Input Report 返回，因此此客户端在写入前注册精确的 inputreport 等待器。
 */
export class CH315KeyboardProtocol {
  private packetSequence = 0
  private disposed = false
  private serial: Promise<void> = Promise.resolve()
  private readonly subscribers = new Set<(report: CH315IncomingReport) => void>()
  private readonly pendingRejectors = new Set<(reason: Error) => void>()
  private readonly passiveInputListener: HidReportListener

  constructor(
    private readonly channel: HidChannel,
    private readonly options: CH315ProtocolOptions = {},
  ) {
    this.passiveInputListener = (reportId, data) => {
      if (reportId !== CH315_HID_REPORT.interrupt && reportId !== CH315_HID_REPORT.bulk) return
      try {
        const packet = decodeCH315Frame(reportId, data)
        if (!packet.checksumValid) return
        this.publish(decodeCH315IncomingReport(packet))
      } catch {
        // 允许页面继续接收后续有效包；原始异常由发起请求的等待器报告。
      }
    }
    this.startListening()
  }

  /**
   * 在通道打开后（尤其 NodeHID）重新挂载常驻监听器。
   * WebHID/NodeHID 的实现都会先移除同一 callback，因此可重复调用。
   */
  public startListening(): void {
    if (this.disposed) {
      throw new DeviceError(
        DeviceErrorCode.NOT_CONNECTED,
        'CH315 protocol client has been disposed',
      )
    }
    this.channel.onInputReport(this.passiveInputListener)
  }

  /** 订阅所有合法的 CH315 普通中断包，返回取消订阅函数。 */
  public onInputReport(listener: (report: CH315IncomingReport) => void): () => void {
    this.subscribers.add(listener)
    return () => this.subscribers.delete(listener)
  }

  /** 移除常驻 inputreport 监听器；关闭页面或 HID 通道前调用。 */
  public dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.channel.offInputReport(this.passiveInputListener)
    if (this.pendingRejectors.size > 0) {
      const error = new DeviceError(
        DeviceErrorCode.NOT_CONNECTED,
        'CH315 protocol client has been disposed',
      )
      for (const reject of [...this.pendingRejectors]) reject(error)
    }
    this.subscribers.clear()
  }

  /** 发送任意已定义 CMD；用于测试页的原始命令入口和未封装命令。 */
  public sendCommand(
    payload: Uint8Array,
    options: CH315CommandOptions = {},
  ): Promise<CH315CommandResponse> {
    if (payload.byteLength === 0) {
      return Promise.reject(
        new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 command payload must include CMD'),
      )
    }
    return this.enqueue(() => this.executeCommand(payload, options))
  }

  public async getFirmwareInfo(
    deviceSelect: number = CH315_DEVICE_SELECT.host,
  ): Promise<CH315FirmwareInfo> {
    assertSingleDeviceSelect(deviceSelect)
    const response = await this.sendCommand(
      new Uint8Array([CH315_COMMAND.getFirmwareInfo, deviceSelect]),
    )
    assertResponseLength(response, 9)
    const versionBytes = [
      response.payload[3],
      response.payload[4],
      response.payload[5],
      response.payload[6],
    ] as const
    return {
      deviceSelect: response.payload[2],
      versionBytes,
      version: formatCH315BcdVersion(versionBytes),
      usbPid: readUint16LE(response.payload, 7),
    }
  }

  public async getMode(): Promise<CH315Mode> {
    const response = await this.sendCommand(new Uint8Array([CH315_COMMAND.getMode]))
    assertResponseLength(response, 3)
    return response.payload[2] as CH315Mode
  }

  public async setMode(mode: CH315Mode): Promise<void> {
    await this.sendCommand(new Uint8Array([CH315_COMMAND.setMode, mode]))
  }

  public async getBattery(
    deviceSelect: number = CH315_DEVICE_SELECT.host,
  ): Promise<CH315BatteryInfo> {
    if (deviceSelect !== CH315_DEVICE_SELECT.host && deviceSelect !== CH315_DEVICE_SELECT.slave) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'Battery command requires host (0x01) or slave (0x02)',
      )
    }
    const response = await this.sendCommand(
      new Uint8Array([CH315_COMMAND.getBattery, deviceSelect]),
    )
    assertResponseLength(response, 5)
    return {
      deviceSelect: response.payload[2],
      valid: response.payload[3] === 1,
      percent: response.payload[4],
    }
  }

  public async getCurrentConfig(): Promise<number> {
    const response = await this.sendCommand(new Uint8Array([CH315_COMMAND.getCurrentConfig]))
    assertResponseLength(response, 3)
    return response.payload[2]
  }

  public async selectConfig(configIndex: number): Promise<void> {
    assertIntegerInRange('configIndex', configIndex, 0, 3)
    await this.sendCommand(new Uint8Array([CH315_COMMAND.selectConfig, configIndex]))
  }

  public async createConfig(configIndex: number, name: string): Promise<void> {
    assertIntegerInRange('configIndex', configIndex, 0, 3)
    const nameBytes = encodeFixedAscii(name, 16)
    await this.sendCommand(new Uint8Array([CH315_COMMAND.addConfig, configIndex, ...nameBytes]))
  }

  public async deleteConfig(configIndex: number): Promise<void> {
    assertIntegerInRange('configIndex', configIndex, 0, 3)
    await this.sendCommand(new Uint8Array([CH315_COMMAND.deleteConfig, configIndex]))
  }

  /** 删除指定用户静态灯效；功能定义提供 15 个槽，SDK 使用 0..14 索引。 */
  public async deleteUserStaticEffect(effectIndex: number): Promise<void> {
    assertIntegerInRange('effectIndex', effectIndex, 0, 14)
    const response = await this.sendCommand(
      new Uint8Array([CH315_COMMAND.deleteUserStaticEffect, effectIndex]),
    )
    assertResponseLength(response, 3)
    if (response.payload[2] !== effectIndex) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `CH315 D6 response index ${response.payload[2]} does not match ${effectIndex}`,
      )
    }
  }

  public async enterCalibration(options: { user: boolean; singleKey: boolean }): Promise<void> {
    const type = (options.user ? 0x01 : 0x00) | (options.singleKey ? 0x02 : 0x00)
    await this.sendCommand(new Uint8Array([CH315_COMMAND.enterCalibration, type]))
  }

  public async saveCalibration(): Promise<void> {
    await this.sendCommand(new Uint8Array([CH315_COMMAND.saveCalibration]))
  }

  public async exitCalibration(): Promise<void> {
    await this.sendCommand(new Uint8Array([CH315_COMMAND.exitCalibration]))
  }

  public async readEeprom(address: number, length: number): Promise<CH315MemoryReadResult> {
    assertAddress(address)
    assertReadLength('EEPROM', length, NORMAL_READ_MAX_LENGTH)
    const response = await this.sendCommand(
      new Uint8Array([
        CH315_COMMAND.readEeprom,
        ...encodeLength(length, this.options.normalReadLengthBytes ?? 2),
        ...uint32LE(address),
      ]),
    )
    assertResponseLength(response, 7)
    const actualLength = response.payload[2]
    const actualAddress = readUint32LE(response.payload, 3)
    assertResponseDataLength(response, 7, actualLength)
    return {
      address: actualAddress,
      length: actualLength,
      data: response.payload.slice(7, 7 + actualLength),
      response,
    }
  }

  public async writeEeprom(address: number, data: Uint8Array): Promise<CH315CommandResponse> {
    assertAddress(address)
    if (data.byteLength > NORMAL_WRITE_SAFE_MAX_LENGTH) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `CH315 EEPROM write is limited to ${NORMAL_WRITE_SAFE_MAX_LENGTH} bytes per packet`,
      )
    }
    return this.sendCommand(
      new Uint8Array([CH315_COMMAND.writeEeprom, data.byteLength, ...uint32LE(address), ...data]),
    )
  }

  public async readFlashBulk(address: number, length: number): Promise<CH315MemoryReadResult> {
    assertAddress(address)
    assertReadLength('bulk Flash', length, BULK_RW_MAX_LENGTH)
    const response = await this.sendBulkCommand(
      new Uint8Array([CH315_COMMAND.readFlashBulk, ...uint16LE(length), ...uint32LE(address)]),
    )
    assertResponseLength(response, 8)
    const actualLength = readUint16LE(response.payload, 2)
    const actualAddress = readUint32LE(response.payload, 4)
    assertResponseDataLength(response, 8, actualLength)
    return {
      address: actualAddress,
      length: actualLength,
      data: response.payload.slice(8, 8 + actualLength),
      response,
    }
  }

  public async writeFlashBulk(address: number, data: Uint8Array): Promise<CH315CommandResponse> {
    assertAddress(address)
    assertLengthInRange('bulk Flash write', data.byteLength, BULK_RW_MAX_LENGTH)
    return this.sendBulkCommand(
      new Uint8Array([
        CH315_COMMAND.writeFlashBulk,
        ...uint16LE(data.byteLength),
        ...uint32LE(address),
        ...data,
      ]),
    )
  }

  /** RF Flash 读响应的长度字段在源协议中存在偏移歧义，因此返回原始命令响应供测试页核对。 */
  public async readRfFlash(
    deviceSelect: number,
    address: number,
    length: number,
  ): Promise<CH315CommandResponse> {
    assertAddress(address)
    assertReadLength('RF Flash', length, NORMAL_READ_MAX_LENGTH)
    assertRfDeviceSelect(deviceSelect)
    return this.sendCommand(
      new Uint8Array([
        CH315_COMMAND.readRfFlash,
        deviceSelect,
        ...encodeLength(length, this.options.rfReadLengthBytes ?? 2),
        ...uint32LE(address),
      ]),
    )
  }

  public async writeRfFlash(
    deviceSelect: number,
    address: number,
    data: Uint8Array,
  ): Promise<CH315CommandResponse> {
    assertAddress(address)
    if (data.byteLength > RF_WRITE_MAX_LENGTH) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `CH315 RF Flash write is limited to ${RF_WRITE_MAX_LENGTH} bytes per packet`,
      )
    }
    assertRfDeviceSelect(deviceSelect)
    return this.sendCommand(
      new Uint8Array([
        CH315_COMMAND.writeRfFlash,
        deviceSelect,
        data.byteLength,
        ...uint32LE(address),
        ...data,
      ]),
    )
  }

  public async runBacklightTest(mode: 0x01 | 0x02 | 0x04 | 0xff): Promise<void> {
    await this.sendCommand(new Uint8Array([CH315_COMMAND.backlightTest, mode]))
  }

  public async startRfTest(options: {
    mode: 0 | 1 | 2 | 3 | 4
    channel: number
    dbm: 8 | 4 | 0 | -4 | -8 | -16
    dataRate: 0 | 1
  }): Promise<void> {
    assertIntegerInRange('RF mode', options.mode, 0, 4)
    assertIntegerInRange('RF channel', options.channel, 0, 80)
    if (![8, 4, 0, -4, -8, -16].includes(options.dbm)) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'RF power must be one of 8, 4, 0, -4, -8, or -16 dBm',
      )
    }
    assertIntegerInRange('RF dataRate', options.dataRate, 0, 1)
    await this.sendCommand(
      new Uint8Array([
        CH315_COMMAND.startRfTest,
        options.mode,
        options.channel,
        options.dbm & 0xff,
        options.dataRate,
      ]),
    )
  }

  public async resetDevice(deviceSelect: number = CH315_DEVICE_SELECT.all): Promise<void> {
    assertResetDeviceSelect(deviceSelect)
    await this.sendCommand(new Uint8Array([CH315_COMMAND.resetDevice, deviceSelect]))
  }

  public async restoreFactoryDefaults(
    deviceSelect: number = CH315_DEVICE_SELECT.all,
  ): Promise<void> {
    assertResetDeviceSelect(deviceSelect)
    await this.sendCommand(
      new Uint8Array([CH315_COMMAND.restoreFactoryDefaults, 0x52, 0x3d, deviceSelect]),
    )
  }

  public async enterFirmwareUpdate(deviceSelect: number = CH315_DEVICE_SELECT.host): Promise<void> {
    assertSingleDeviceSelect(deviceSelect)
    await this.sendCommand(new Uint8Array([CH315_COMMAND.enterFirmwareUpdate, deviceSelect]))
  }

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const result = this.serial.then(task, task)
    this.serial = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }

  private async executeCommand(
    payload: Uint8Array,
    options: CH315CommandOptions,
  ): Promise<CH315CommandResponse> {
    return this.executeFramedCommand(CH315_HID_REPORT.interrupt, payload, options)
  }

  private async sendBulkCommand(
    payload: Uint8Array,
    options: CH315CommandOptions = {},
  ): Promise<CH315CommandResponse> {
    if (payload.byteLength > CH315_HID_REPORT.bulkPayloadMaxBytes) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `CH315 bulk command exceeds ${CH315_HID_REPORT.bulkPayloadMaxBytes} bytes`,
      )
    }
    return this.enqueue(() => this.executeFramedCommand(CH315_HID_REPORT.bulk, payload, options))
  }

  private async executeFramedCommand(
    reportId: number,
    payload: Uint8Array,
    options: CH315CommandOptions,
  ): Promise<CH315CommandResponse> {
    if (this.disposed) {
      throw new DeviceError(
        DeviceErrorCode.NOT_CONNECTED,
        'CH315 protocol client has been disposed',
      )
    }

    const timeoutMs = options.timeoutMs ?? 100
    const maxRetries = options.maxRetries ?? 3
    const requireSuccess = options.requireSuccess ?? true
    const command = payload[0]
    let lastError: unknown

    for (let retry = 0; retry <= maxRetries; retry += 1) {
      const packetSequence = this.nextPacketSequence()
      const frame =
        reportId === CH315_HID_REPORT.interrupt
          ? encodeCH315InterruptFrame(packetSequence, payload, reportId)
          : encodeCH315BulkFrame(packetSequence, payload, reportId)

      try {
        const packet = await this.waitForCommandResponse(reportId, frame, command, timeoutMs)
        const ack = readAck(packet, command)
        const response: CH315CommandResponse = {
          command,
          ack,
          packet,
          payload: packet.payload,
          attempts: retry + 1,
        }

        if (ack === CH315_ACK.busy || ack === CH315_ACK.retry) {
          lastError = new CH315TimeoutError(
            `CH315 command 0x${command.toString(16)} returned ACK ${ack}`,
          )
        } else if (requireSuccess && ack !== CH315_ACK.success) {
          throw new DeviceError(
            DeviceErrorCode.READ_FAILED,
            `CH315 command 0x${command.toString(16)} failed with ACK ${ack}`,
          )
        } else {
          return response
        }
      } catch (error) {
        if (!isTimeoutError(error)) throw error
        lastError = error
      }

      if (retry < maxRetries) await delay(100)
    }

    throw new DeviceError(
      DeviceErrorCode.TIMEOUT,
      lastError instanceof Error
        ? lastError.message
        : `CH315 command 0x${command.toString(16)} timed out`,
      lastError,
    )
  }

  private waitForCommandResponse(
    reportId: number,
    frame: Uint8Array,
    command: number,
    timeoutMs: number,
  ): Promise<CH315InterruptPacket> {
    return new Promise<CH315InterruptPacket>((resolve, reject) => {
      let settled = false

      const listener: HidReportListener = (incomingReportId, data) => {
        if (incomingReportId !== reportId) return
        try {
          const packet = decodeCH315Frame(incomingReportId, data)
          if (!packet.checksumValid || packet.payload[0] !== command) return
          cleanup()
          resolve(packet)
        } catch {
          // 其它复合接口或未完成的包不应中断当前命令等待。
        }
      }

      const rejectPending = (reason: Error) => {
        cleanup()
        reject(reason)
      }

      const cleanup = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        this.channel.offInputReport(listener)
        this.pendingRejectors.delete(rejectPending)
      }

      const timer = setTimeout(() => {
        rejectPending(
          new CH315TimeoutError(
            `CH315 command 0x${command.toString(16)} input report timed out after ${timeoutMs}ms`,
          ),
        )
      }, timeoutMs)

      this.pendingRejectors.add(rejectPending)
      this.channel.onInputReport(listener)
      this.channel.sendOutputReport(reportId, frame).catch((error: unknown) => {
        cleanup()
        reject(error)
      })
    })
  }

  private nextPacketSequence(): number {
    const sequence = this.packetSequence
    this.packetSequence = (this.packetSequence + 1) & 0xff
    return sequence
  }

  private publish(report: CH315IncomingReport): void {
    for (const subscriber of this.subscribers) subscriber(report)
  }
}

/** 构造 WebHID 所需的 511 B 批量包数据。 */
export function encodeCH315BulkFrame(
  packetSequence: number,
  payload: Uint8Array,
  reportId: number = CH315_HID_REPORT.bulk,
): Uint8Array {
  if (payload.byteLength > CH315_HID_REPORT.bulkPayloadMaxBytes) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `CH315 bulk payload exceeds ${CH315_HID_REPORT.bulkPayloadMaxBytes} bytes`,
    )
  }
  const frame = new Uint8Array(CH315_HID_REPORT.bulkDataBytes)
  frame[0] = packetSequence & 0xff
  frame[1] = payload.byteLength & 0xff
  frame[2] = (payload.byteLength >>> 8) & 0xff
  frame.set(payload, 3)
  const checksumIndex = 3 + payload.byteLength
  frame[checksumIndex] = calculateCH315Checksum(reportId, frame.subarray(0, checksumIndex))
  return frame
}

/** 按 Report ID 解析普通或批量 CH315 帧。 */
export function decodeCH315Frame(
  reportId: number,
  data: DataView | Uint8Array,
): CH315InterruptPacket {
  if (reportId === CH315_HID_REPORT.interrupt) return decodeCH315InterruptFrame(reportId, data)
  if (reportId === CH315_HID_REPORT.bulk) return decodeCH315BulkFrame(reportId, data)
  throw new DeviceError(
    DeviceErrorCode.READ_FAILED,
    `Unsupported CH315 report id 0x${reportId.toString(16)}`,
  )
}

/** 解析 0x07 批量帧；兼容 511 B WebHID data 与含 Report ID 的 512 B 抓包。 */
export function decodeCH315BulkFrame(
  reportId: number,
  data: DataView | Uint8Array,
): CH315InterruptPacket {
  const input =
    data instanceof DataView ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : data
  const frame = stripCapturedReportId(reportId, input, CH315_HID_REPORT.bulkPacketBytes)
  if (frame.byteLength < 4) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 bulk frame is shorter than header')
  }
  const payloadLength = frame[1] | (frame[2] << 8)
  if (payloadLength > CH315_HID_REPORT.bulkPayloadMaxBytes) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 bulk payload exceeds protocol limit')
  }
  const checksumIndex = 3 + payloadLength
  if (checksumIndex >= frame.byteLength) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 bulk frame is truncated')
  }
  const checksum = frame[checksumIndex]
  return {
    reportId,
    packetSequence: frame[0],
    payloadLength,
    payload: frame.slice(3, checksumIndex),
    checksum,
    checksumValid: checksum === calculateCH315Checksum(reportId, frame.subarray(0, checksumIndex)),
    raw: frame.slice(0, checksumIndex + 1),
  }
}

/** 将 F0 主动上报解码为结构化事件；未知载荷保留为 packet。 */
export function decodeCH315IncomingReport(packet: CH315InterruptPacket): CH315IncomingReport {
  const payload = packet.payload
  if (payload[0] !== CH315_COMMAND.userReport || payload.byteLength < 2) {
    return { type: 'packet', packet }
  }

  if (payload[1] === 0x01 && payload.byteLength >= 20) {
    return {
      type: 'user-status',
      packet,
      status: {
        connectionMode: payload[2] & 0x0f,
        configIndex: (payload[2] >>> 4) & 0x0f,
        backlightMode: payload[3],
        backlightLevel: payload[4],
        backlightSpeed: payload[5],
        backlightColor: payload[6],
        sleepMinutes: payload[7],
        reportRate: payload[8],
        batteryPercent: payload[9],
        functionWord: readUint16LE(payload, 10),
        usbPid: readUint16LE(payload, 12),
        deviceStatus: payload[14],
        connectionRssi: payload[15],
        sideLightMode: payload[16],
        sideLightLevel: payload[17],
        sideLightSpeed: payload[18],
        sideLightColor: payload[19],
      },
    }
  }

  if (payload[1] === 0x02 && payload.byteLength >= 4) {
    const keys: CH315CalibrationKeyReport[] = []
    for (let offset = 4; offset + 12 <= payload.byteLength; offset += 12) {
      keys.push({
        keySerial: payload[offset],
        calibrationStatus: payload[offset + 1],
        zeroDelta: readUint16LE(payload, offset + 2),
        maxDelta: readUint16LE(payload, offset + 4),
        lta: readUint16LE(payload, offset + 6),
        normalizedDelta: readUint16LE(payload, offset + 8),
        release: readUint16LE(payload, offset + 10),
      })
    }
    return {
      type: 'calibration',
      packet,
      packetSequence: payload[2],
      releaseFlag: payload[3],
      keys,
    }
  }

  return { type: 'packet', packet }
}

function readAck(packet: CH315InterruptPacket, command: number): CH315Ack {
  if (packet.payload.byteLength < 2 || packet.payload[0] !== command) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 response is missing command ACK')
  }
  return packet.payload[1] as CH315Ack
}

function assertResponseLength(response: CH315CommandResponse, requiredLength: number): void {
  if (response.payload.byteLength < requiredLength) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `CH315 command 0x${response.command.toString(16)} response is shorter than ${requiredLength} bytes`,
    )
  }
}

function assertResponseDataLength(
  response: CH315CommandResponse,
  headerLength: number,
  declaredLength: number,
): void {
  if (response.payload.byteLength < headerLength + declaredLength) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `CH315 command 0x${response.command.toString(16)} declares ${declaredLength} data bytes but the response is truncated`,
    )
  }
}

function assertRfDeviceSelect(deviceSelect: number): void {
  if (deviceSelect !== CH315_DEVICE_SELECT.hostRf && deviceSelect !== CH315_DEVICE_SELECT.slaveRf) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      'RF Flash command requires exactly one RF device select (0x04 or 0x08)',
    )
  }
}

function assertSingleDeviceSelect(deviceSelect: number): void {
  if (
    deviceSelect !== CH315_DEVICE_SELECT.host &&
    deviceSelect !== CH315_DEVICE_SELECT.slave &&
    deviceSelect !== CH315_DEVICE_SELECT.hostRf &&
    deviceSelect !== CH315_DEVICE_SELECT.slaveRf
  ) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      'CH315 command requires exactly one device select (0x01, 0x02, 0x04, or 0x08)',
    )
  }
}

function assertResetDeviceSelect(deviceSelect: number): void {
  if (deviceSelect !== CH315_DEVICE_SELECT.all) assertSingleDeviceSelect(deviceSelect)
}

function assertAddress(address: number): void {
  if (!Number.isInteger(address) || address < 0 || address > 0xffffffff) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `Invalid CH315 address: ${address}`)
  }
}

function assertReadLength(name: string, length: number, max: number): void {
  if (!Number.isInteger(length) || length < 0 || length > max || length % 4 !== 0) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `${name} length must be a 4-byte multiple between 0 and ${max}`,
    )
  }
}

function assertLengthInRange(name: string, length: number, max: number): void {
  if (!Number.isInteger(length) || length < 0 || length > max) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `${name} length must be between 0 and ${max}`,
    )
  }
}

function assertIntegerInRange(name: string, value: number, min: number, max: number): void {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `${name} must be an integer in ${min}..${max}`,
    )
  }
}

function uint16LE(value: number): [number, number] {
  return [value & 0xff, (value >>> 8) & 0xff]
}

function encodeLength(value: number, bytes: 1 | 2): number[] {
  return bytes === 1 ? [value & 0xff] : uint16LE(value)
}

function uint32LE(value: number): [number, number, number, number] {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff]
}

function readUint16LE(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8)
}

function readUint32LE(data: Uint8Array, offset: number): number {
  return (
    (data[offset] |
      (data[offset + 1] << 8) |
      (data[offset + 2] << 16) |
      (data[offset + 3] << 24)) >>>
    0
  )
}

function encodeFixedAscii(value: string, bytes: number): Uint8Array {
  if (value.length > bytes || !/^[\x20-\x7e]*$/.test(value)) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `CH315 text must contain at most ${bytes} printable ASCII characters`,
    )
  }
  const result = new Uint8Array(bytes)
  for (let index = 0; index < value.length; index += 1) {
    result[index] = value.charCodeAt(index)
  }
  return result
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

function stripCapturedReportId(
  reportId: number,
  input: Uint8Array,
  capturedPacketBytes: number,
): Uint8Array {
  if (input.byteLength !== capturedPacketBytes) return input
  if (input[0] !== reportId) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `CH315 captured frame report id 0x${input[0].toString(16)} does not match 0x${reportId.toString(16)}`,
    )
  }
  return input.slice(1)
}

class CH315TimeoutError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'CH315TimeoutError'
  }
}

function isTimeoutError(error: unknown): error is Error {
  return (
    error instanceof CH315TimeoutError ||
    (error instanceof DeviceError && error.code === DeviceErrorCode.TIMEOUT)
  )
}
